import { useQuery, queryOptions } from "@tanstack/react-query";
import { listShippingConfig, type ShippingZoneDTO, type ShippingCountryDTO } from "@/lib/shipping.functions";

export const shippingConfigQuery = () =>
  queryOptions({
    queryKey: ["shipping", "config"],
    queryFn: () => listShippingConfig(),
    staleTime: 5 * 60_000,
  });

export function useShippingConfig() {
  return useQuery(shippingConfigQuery());
}

export function formatEuro(cents: number | null | undefined): string {
  if (cents == null) return "—";
  const eur = cents / 100;
  return eur % 1 === 0 ? `€${eur.toFixed(0)}` : `€${eur.toFixed(2)}`;
}

/** Returns the "headline" free-shipping threshold shown in announcement bars.
 *  Uses the lowest non-null threshold across zones so copy stays truthful. */
export function headlineFreeOver(zones: ShippingZoneDTO[] | undefined): number | null {
  if (!zones) return null;
  const vals = zones.map((z) => z.free_over_cents).filter((v): v is number => typeof v === "number");
  if (vals.length === 0) return null;
  return Math.min(...vals);
}

/** The catch-all zone: prefer "world", otherwise the last zone by sort order. */
export function fallbackZone(zones: ShippingZoneDTO[] | undefined): ShippingZoneDTO | null {
  if (!zones || zones.length === 0) return null;
  const world = zones.find((z) => z.code === "world");
  if (world) return world;
  return [...zones].sort((a, b) => a.sort_order - b.sort_order)[zones.length - 1] ?? null;
}

export function zoneForCountry(
  iso2: string | undefined,
  countries: ShippingCountryDTO[] | undefined,
  zones: ShippingZoneDTO[] | undefined,
): ShippingZoneDTO | null {
  if (!iso2 || !zones) return null;
  const c = countries?.find((x) => x.iso2 === iso2.toUpperCase());
  // Unknown / unmapped country → always fall back to the catch-all zone
  // so checkout never tells a customer we don't ship there.
  if (!c) return fallbackZone(zones);
  return zones.find((z) => z.code === c.zone_code) ?? fallbackZone(zones);
}